import { AsyncPipe } from "@angular/common";
import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { InputComponent } from "@components/input/input.component";
import { MessagePanelComponent } from "@components/message-panel/message-panel.component";
import { PageComponent } from "@components/page/page.component";
import { TabComponent } from "@components/tabs/tab/tab.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import { BehaviorSubject, Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { Viol3ArbetsorderResponseDTO } from "~app/common/models/integration/viol3/Viol3ArbetsorderResponseDTO";
import { CommandService } from "~app/common/services/cqs/command.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { EventHubService } from "~app/common/services/eventhub.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { SendViol3ArbetsorderCommand } from "~app/features/administration/systemadministration/tabs/sysadm-viol3/commands/SendViol3ArbetsorderCommand";
import { Viol3ValidationEventMessage } from "~app/features/administration/systemadministration/tabs/sysadm-viol3/events/Viol3ValidationEventMessage";
import { IntegrationService } from "~app/features/administration/systemadministration/tabs/sysadm-viol3/service/integration.service";
import { ArbetsorderMonitorService } from "~app/features/planera/services/arbetsorder-monitor.service";
import { InitiateArbetsorderViol3SynchronizationCommand } from "./commands/InitiateArbetsorderViol3SynchronizationCommand";
import { InmatningtestComponent } from "./inmatningtest/inmatningtest.component";
import { LeveranserComponent } from "./leveranser/leveranser.component";
import { Viol3xmlComponent } from "./viol3xml/viol3xml.component";
import { VirkesorderHistorikComponent } from "./virkesorder-historik/virkesorder-historik.component";
import { VirkesorderViol3PreviewComponent } from "./virkesorder-viol3-preview/virkesorder-viol3-preview.component";

@Component({
    selector: "rask-sysadm-viol3",
    templateUrl: "./sysadm-viol3.component.html",
    styleUrls: ["./sysadm-viol3.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        InputComponent,
        FormsModule,
        ButtonComponent,
        ActionButtonComponent,
        MessagePanelComponent,
        TabsComponent,
        TabComponent,
        Viol3xmlComponent,
        VirkesorderHistorikComponent,
        VirkesorderViol3PreviewComponent,
        LeveranserComponent,
        InmatningtestComponent,
        AsyncPipe
    ]
})
export class SysadmViol3Component implements OnInit, OnDestroy {
    readonly webbinstallningNyckel = "viol3.testklient.senasteao";

    alerts$ = new BehaviorSubject<IMessageModel[]>([]);
    arbetsorderBeteckningSearchStr = "";
    isWorking = false;
    sagaMessage = "";
    selectedArbetsorder: Viol3ArbetsorderResponseDTO;

    private readonly arbetsorderMonitorService = inject(ArbetsorderMonitorService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly eventHubService = inject(EventHubService);
    private readonly eventService = inject(EventService);
    private readonly integrationService = inject(IntegrationService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly webinstallningarService = inject(WebinstallningarService);

    canSend(): boolean {
        return !ObjectUtils.isNullOrUndefined(this.selectedArbetsorder);
    }

    canSync(): boolean {
        return !ObjectUtils.isNullOrUndefined(this.selectedArbetsorder);
    }

    ngOnDestroy(): void {
        if(this.selectedArbetsorder) {
            this.arbetsorderMonitorService.stop();
            this.eventHubService.unregisterListener(`arbetsorder/${this.selectedArbetsorder.arbetsorderId.toString()}`);
        }
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit() {
        this.webinstallningarService.getAsync(this.webbinstallningNyckel).then((installning: any) => {
            if(installning) {
                this.arbetsorderBeteckningSearchStr = installning.senasteSokning;
            }
        });

        this.eventService.subscribeEvent<Viol3ValidationEventMessage>(Viol3ValidationEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(event => event.arbetsorderId === this.selectedArbetsorder.arbetsorderId))
            .subscribe((event) => {
                const v = event.validationMessages.map(x => ({
                    severity: this.mapGenericMessageSeverity(x.severity),
                    message: x.message,
                    anchor: ""
                }));
                this.alerts$.next(v);
            });
    }

    send() {
        const command = new SendViol3ArbetsorderCommand(this.selectedArbetsorder.arbetsorderId);
        const commandService = this.commandService.createInstance();
        commandService.addTransactionCommand(command);
        commandService.pushCommandQueueTransaction();
    }

    sokArbetsorder() {
        if(!ObjectUtils.isNullOrUndefined(this.arbetsorderBeteckningSearchStr)) {
            if(this.selectedArbetsorder) {
                this.eventHubService.unregisterListener(`arbetsorder/${this.selectedArbetsorder.arbetsorderId.toString()}`);
            }

            this.webinstallningarService.set(this.webbinstallningNyckel, { senasteSokning:this.arbetsorderBeteckningSearchStr });
            const getArbetsorderResult = this.integrationService.getArbetsorderByArbetsorderBeteckning(this.arbetsorderBeteckningSearchStr);
            this.busyService.addWork(getArbetsorderResult).subscribe((res: Viol3ArbetsorderResponseDTO) => {
                if (this.selectedArbetsorder) {
                    this.arbetsorderMonitorService.stop();
                    this.eventHubService.unregisterListener(`arbetsorder/${this.selectedArbetsorder.arbetsorderId.toString()}`);
                }
                this.selectedArbetsorder = res;
                this.eventHubService.registerListener(`arbetsorder/${this.selectedArbetsorder.arbetsorderId.toString()}`);
                this.arbetsorderMonitorService.start(this.selectedArbetsorder.arbetsorderId);
            });
        }
    }

    sync() {
        const command = new InitiateArbetsorderViol3SynchronizationCommand(x => x.arbetsorderId = this.selectedArbetsorder.arbetsorderId);
        const commandService = this.commandService.createInstance();
        commandService.addTransactionCommand(command);
        commandService.pushCommandQueueTransaction();
    }

    private mapGenericMessageSeverity(source: string): MessageSeverity {
        switch (source) {
            case "Error":
                return MessageSeverity.Error;
            case "Warning":
                return MessageSeverity.Warning;
            case "Info":
                return MessageSeverity.Info;
            default:
                return MessageSeverity.Info;
        }
    }
}
